const libPictProvider = require('pict-provider');

const { buildSamplePlanModel, restampRollups, resolvePlimtCounts } = require('./SamplePlan-Model.js');
const { procedureForTest } = require('./SamplePlan-MSM-Map.js');

// The Intent vocabulary the legacy editor offers. Free text is still accepted (this is a combobox seed, not a gate).
const INTENT_OPTIONS = [ 'Ongoing', 'Final', 'Special', 'Other' ];
// The SamplePlan config defaults the Vue app merges over (Configuration/For/SamplePlan/HeadLight). Test selector on,
// rate toggle off, unless the customer's config says otherwise.
const DEFAULT_FEATURES = { ShowTestSelector: true, ShowRateToggle: false };

// Graduated satisfaction tones: near-complete reads green, in-progress amber, well-behind red.
const fBarTone = (pPercent) => ((pPercent >= 90) ? 'ok' : ((pPercent >= 50) ? 'warn' : 'bad'));

// A static resolver over an in-memory array — the shape pict-dataexplorer expects from a custom source.
const fStaticResolve = (pRows, pOptions) =>
{
	const tmpRows = pRows || [];
	if (pOptions && pOptions.CountOnly) { return { count: tmpRows.length }; }
	const tmpBegin = (pOptions && pOptions.begin) || 0;
	const tmpCount = (pOptions && pOptions.count) || tmpRows.length;
	return { records: tmpRows.slice(tmpBegin, tmpBegin + tmpCount), hasMore: (tmpBegin + tmpCount) < tmpRows.length };
};

// Section / Line Item / Material rollup → a samples bar (count rules) + the count of sample-plan entries nested here.
const fRollupMeters = (pRecord) =>
{
	const tmpMeters = [];
	if (pRecord.SamplesRequired > 0)
	{
		const tmpPercent = Math.round((pRecord.SamplesCreated / pRecord.SamplesRequired) * 100);
		tmpMeters.push({ Kind: 'bar', Percent: tmpPercent, Tone: fBarTone(tmpPercent), Label: `${pRecord.SamplesCreated}/${pRecord.SamplesRequired}`, Title: `${pRecord.SamplesCreated} of ${pRecord.SamplesRequired} samples created` });
	}
	if (pRecord.EntryCount > 0) { tmpMeters.push({ Kind: 'pill', Tone: 'muted', Text: `${pRecord.EntryCount} ${pRecord.EntryCount === 1 ? 'entry' : 'entries'}`, Title: `${pRecord.EntryCount} sample plan ${pRecord.EntryCount === 1 ? 'entry' : 'entries'} nested here` }); }
	return tmpMeters;
};

// A single rule: certificate rules show a certificate pill, once-per-project tests show a "1 / project" pill, and
// count rules show a samples-progress bar.
const fRuleMeters = (pRecord) =>
{
	if (pRecord.Kind === 'cert') { return [ { Kind: 'pill', Tone: 'muted', Text: 'by certificate', Title: 'Accepted by certificate of delivery' } ]; }
	if (pRecord.Kind === 'perproject') { return [ { Kind: 'pill', Tone: 'muted', Text: '1 / project', Title: 'Sampled once per project' } ]; }
	const tmpPercent = (pRecord.Required > 0) ? Math.round((pRecord.Created / pRecord.Required) * 100) : 0;
	return [ { Kind: 'bar', Percent: tmpPercent, Tone: fBarTone(tmpPercent), Label: `${pRecord.Created}/${pRecord.Required}`, Title: `${pRecord.Created} of ${pRecord.Required} samples created` } ];
};

// The columns each tier offers in its "Columns" chooser (surfaced as chips on the rows). These tiers are
// SYNTHESIZED in memory (custom Resolvers over the loaded plan — SamplePlan-Model), so there is no Meadow
// entity schema to discover: the explorer's default schema source would GET `/1.0/<Entity>/Schema` — a 404
// for the synthetic 'Rule' tier ("Endpoint does not exist!"), and for the real entities it offers columns the
// in-memory records don't carry (they render blank when checked, which is why only Description/Units showed).
// So we feed the chooser this explicit list of real, human-facing fields present on each tier's records
// instead (see chooserColumnsForEntity + SamplePlan.onAfterRender). Each tier's Display Title field is
// filtered out of the chooser by the explorer itself. Only scalar fields — a chip stringifies its value.
const SAMPLE_PLAN_TIER_COLUMNS =
{
	Section:  [ 'Name', 'LineItemCount', 'EntryCount', 'SamplesRequired', 'SamplesCreated', 'CertCount', 'CertPending', 'SatisfactionText' ],
	LineItem: [ 'Number', 'Description', 'Units', 'ExpectedQty', 'InstalledQty', 'EntryCount', 'SamplesRequired', 'SamplesCreated', 'CertCount', 'CertPending', 'SatisfactionText' ],
	Material: [ 'MaterialCode', 'EntryCount', 'SamplesRequired', 'SamplesCreated', 'CertCount', 'CertPending', 'SatisfactionText' ],
	Rule:     [ 'TestHash', 'SampleType', 'Kind', 'Frequency', 'Rate', 'ConversionFactor', 'Units', 'Required', 'Planned', 'Created', 'Passing', 'Intent', 'SamplingInstructions', 'Remarks', 'SatisfactionText' ],
};

/**
 * The Sample Plan editor's data/logic provider. Loads a project's whole plan once (SamplePlan/Lite, whose server-computed counts it uses verbatim), builds the in-memory Section→LineItem→Material→Rule model + satisfaction (SamplePlan-Model),
 * and hands pict-dataexplorer an in-memory ExplorerConfig (custom Resolvers + RowMeters + OnNodeSelect). The
 * SamplePlan view owns the chrome + the master/detail render; this provider owns the model, the explorer
 * config, selection → detail data, and the layered AcademyContent docs (fetched by hash).
 */
class SamplePlanProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._idProject = null;
		this._model = null;
		this._explorerConfig = null;
		this._selected = null;
		this._docsMode = true;
		this._academyCache = {};
		// Edit-mode support: the model's rule projection is lossy, so full edit works off the raw PLIMT rows. We retain the raw
		// line items + materials (for the edit form vocabularies) + the fresh Lite PLIMTs (whose server-computed counts the editor reads).
		this._rawLineItemByID = {};
		this._rawMaterialByID = {};
		this._unitsOptions = [];
		this._features = null;
		this._testOptions = null;
		this._sampleTypes = null;
		this._rawRuleCache = {};
		// Mutable tree indexes (populated by _buildExplorerConfig) so add-entry / add-material can splice nodes in place.
		this._lineItemsBySection = {};
		this._materialsByLineItem = {};
		this._rulesByMaterialKey = {};
		// Contract-id → spec year, resolved lazily for the front-door project list and cached across searches.
		this._contractSpecYearCache = {};
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get workspaceState()
	{
		if (!this.pict.AppData.SamplePlan) { this.pict.AppData.SamplePlan = { Loading: false, Message: '', HasModel: false, ProjectName: '', ProjectCode: '', PlanText: '' }; }
		return this.pict.AppData.SamplePlan;
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_render()
	{
		if (this.pict.views['SamplePlan']) { this.pict.views['SamplePlan'].render(); }
	}

	getExplorerConfig() { return this._explorerConfig; }

	/**
	 * The "Columns" chooser options for a synthesized tier — the real fields on that tier's in-memory records.
	 * Wired into the explorer as its SchemaSource so the chooser never fetches `/1.0/<Entity>/Schema` (there is
	 * no Meadow entity behind these tiers; the synthetic 'Rule' tier's endpoint 404s). Returns [] for unknown
	 * tiers, which the explorer treats as "no chooser".
	 * @param {String} pEntity
	 * @returns {Array<String>}
	 */
	chooserColumnsForEntity(pEntity) { return SAMPLE_PLAN_TIER_COLUMNS[pEntity] || []; }
	get model() { return this._model; }
	get selected() { return this._selected; }
	get docsMode() { return this._docsMode; }

	// --- Loading ------------------------------------------------------------------------------------

	/**
	 * Load a project's sample plan by ID, build the model + the explorer config, then re-render (the view's
	 * onAfterRender mounts the explorer once the config is present).
	 * @param {Number} pIDProject
	 */
	async loadAsync(pIDProject)
	{
		const tmpState = this.workspaceState;
		this._idProject = pIDProject;
		this._model = null;
		this._explorerConfig = null;
		this._selected = null;
		this._academyCache = {};
		this._rawLineItemByID = {};
		this._rawMaterialByID = {};
		this._unitsOptions = [];
		this._features = null;
		this._testOptions = null;
		this._rawRuleCache = {};
		tmpState.Loading = true;
		tmpState.Message = '';
		tmpState.HasModel = false;
		this._render();

		try
		{
			const tmpLite = await this._getJSON(`SamplePlan/Lite/By/IDProject/${pIDProject}`);
			// SamplePlan/Lite carries the BOM (MLIJ) + rules (PLIMT) — which reference IDMaterial + IDTest — but omits
			// the Material + Test entities. Fetch + inject them so the model can name/code materials and surface test
			// hashes (else materials show "Material <id>" and test hashes are unavailable).
			tmpLite.Materials = await this._fetchByIDs('Materials', 'IDMaterial', this._collectIDs(tmpLite, 'IDMaterial', [ 'MaterialLineItemJoins', 'ProjectLineItemMaterialTests' ]));
			tmpLite.Tests = await this._fetchByIDs('Tests', 'IDTest', this._collectIDs(tmpLite, 'IDTest', [ 'ProjectLineItemMaterialTests' ]));
			// The SamplePlan/Lite payload already carries the SERVER-computed ComputedRequired/PlannedSampleCount on each
			// PLIMT (the endpoint runs the authoritative PLIMTCalculator) — the model reads those directly; nothing is
			// computed client-side.
			const tmpModel = buildSamplePlanModel(tmpLite);
			this._model = tmpModel;
			this._explorerConfig = this._buildExplorerConfig(tmpModel);

			// Retain the raw payload pieces edit mode needs: the materials' UnitsImperial is the Units/Sample Units
			// dropdown vocabulary; line items carry the display quantities.
			(tmpLite.LineItems || []).forEach((pLineItem) => { this._rawLineItemByID[pLineItem.IDLineItem] = pLineItem; });
			(tmpLite.Materials || []).forEach((pMaterial) => { this._rawMaterialByID[pMaterial.IDMaterial] = pMaterial; });
			// Cache the FRESH Lite PLIMTs (they carry the server-recomputed Computed*SampleCount) as the raw edit rows,
			// so the editor's computed readouts match the tree instead of the stale ProjectLineItemMaterialTest table.
			(tmpLite.ProjectLineItemMaterialTests || []).forEach((pPLIMT) => { if (pPLIMT.IDProjectLineItemMaterialTest != null) { this._rawRuleCache[pPLIMT.IDProjectLineItemMaterialTest] = pPLIMT; } });
			this._unitsOptions = this._distinctUnits(tmpLite.Materials);
			// Feature flags gate the Rate toggle vs. numeric input and the Test/Intent selectors — best-effort, defaulted.
			this._features = await this._loadFeaturesAsync();

			tmpState.Loading = false;
			tmpState.HasModel = true;
			tmpState.ProjectName = (tmpModel.project && (tmpModel.project.Name || tmpModel.project.Description)) || `Project ${pIDProject}`;
			tmpState.ProjectCode = (tmpModel.project && (tmpModel.project.ExternalProjectID || tmpModel.project.ProjectCode)) || '';
			tmpState.PlanText = tmpModel.plan ? tmpModel.plan.SatisfactionText : '';
			tmpState.SectionCount = tmpModel.sections.length;
			tmpState.LineItemCount = tmpModel.lineItems.length;
			tmpState.RuleCount = tmpModel.rules.length;
			this._render();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.HasModel = false;
			tmpState.Message = `Could not load the sample plan for project ${pIDProject}: ${pError && pError.message ? pError.message : pError}`;
			this.pict.log.error(`SamplePlan load failed for project ${pIDProject}`, pError);
			this._render();
		}
	}

	/** Collect the distinct truthy values of pField across the named Lite collections. */
	_collectIDs(pLite, pField, pCollections)
	{
		const tmpMap = {};
		pCollections.forEach((pName) => { (pLite[pName] || []).forEach((pRow) => { if (pRow[pField]) { tmpMap[pRow[pField]] = true; } }); });
		return Object.keys(tmpMap);
	}

	/**
	 * Batch-fetch entity records by a list of ids — SamplePlan/Lite carries only the foreign keys (IDMaterial,
	 * IDTest), not the Material / Test entities, so the model can't name/code them without this. FBL (filter-by-
	 * list) IN, chunked to keep the URL bounded; failures degrade (names/codes fall back) rather than break load.
	 */
	async _fetchByIDs(pEntityPlural, pIDField, pIDs)
	{
		if (!pIDs || (pIDs.length === 0)) { return []; }
		const tmpRecords = [];
		const tmpChunkSize = 200;
		for (let i = 0; i < pIDs.length; i += tmpChunkSize)
		{
			const tmpChunk = pIDs.slice(i, i + tmpChunkSize);
			try
			{
				const tmpRows = await this._getJSON(`${pEntityPlural}/FilteredTo/FBL~${pIDField}~INN~${tmpChunk.join(',')}/0/${tmpChunk.length}`);
				if (Array.isArray(tmpRows)) { tmpRecords.push.apply(tmpRecords, tmpRows); }
			}
			catch (pError)
			{
				this.pict.log.warn(`SamplePlan: could not load ${tmpChunk.length} ${pEntityPlural}`, pError);
			}
		}
		return tmpRecords;
	}

	// --- Explorer config (in-memory) ----------------------------------------------------------------

	_buildExplorerConfig(pModel)
	{
		// Provider-level (MUTABLE) indexes so add-entry / add-material can splice new nodes in and refresh just the
		// affected folder in place (the resolvers read these live), without rebuilding or collapsing the whole tree.
		this._lineItemsBySection = {};
		this._materialsByLineItem = {};
		this._rulesByMaterialKey = {};
		for (let i = 0; i < pModel.lineItems.length; i++) { const li = pModel.lineItems[i]; (this._lineItemsBySection[li.IDSection] = this._lineItemsBySection[li.IDSection] || []).push(li); }
		for (let i = 0; i < pModel.materials.length; i++) { const m = pModel.materials[i]; (this._materialsByLineItem[m.IDLineItem] = this._materialsByLineItem[m.IDLineItem] || []).push(m); }
		for (let i = 0; i < pModel.rules.length; i++) { const r = pModel.rules[i]; (this._rulesByMaterialKey[r.MaterialKey] = this._rulesByMaterialKey[r.MaterialKey] || []).push(r); }

		return {
			PageSize: 50,
			MaxDepth: 8,
			OnNodeSelect: (pSelection) => this.onNodeSelect(pSelection),
			// A node whose only child is a single collection (LineItem→Materials, Material→Tests) auto-opens that
			// collection, so drilling in doesn't cost an extra click per level.
			AutoExpandSoleChild: true,
			Roots:
			[
				{ Label: 'By Pay-Item Section', Entity: 'Section' },
				{ Label: 'By Line Item', Entity: 'LineItem' },
			],
			Entities:
			{
				Section:
				{
					IDField: 'IDSection',
					Display: { Title: 'TreeLabel', Subtitle: '{~D:Record.LineItemCount~} line items' },
					RowMeters: fRollupMeters,
					Resolver: (pOptions) => fStaticResolve(pModel.sections, pOptions),
					Children: [ { Label: 'Line Items', Entity: 'LineItem', Resolve: 'count', Resolver: (pSection, pOptions) => fStaticResolve(this._lineItemsBySection[pSection.IDSection], pOptions) } ],
				},
				LineItem:
				{
					IDField: 'IDLineItem',
					Display: { Title: 'Name', Subtitle: '{~D:Record.Description~}' },
					RowMeters: fRollupMeters,
					Resolver: (pOptions) => fStaticResolve(pModel.lineItems, pOptions),
					Children: [ { Label: 'Materials', Entity: 'Material', Resolve: 'count', Resolver: (pLineItem, pOptions) => fStaticResolve(this._materialsByLineItem[pLineItem.IDLineItem], pOptions) } ],
				},
				Material:
				{
					IDField: 'Key',
					Display: { Title: 'Name', Subtitle: '{~D:Record.MaterialCode~}' },
					RowMeters: fRollupMeters,
					Children: [ { Label: 'Tests', Entity: 'Rule', Resolve: 'count', Resolver: (pMaterial, pOptions) => fStaticResolve(this._rulesByMaterialKey[pMaterial.Key], pOptions) } ],
				},
				Rule:
				{
					IDField: 'IDRule',
					Display: { Title: 'Title', Subtitle: '{~D:Record.TestLabel~}' },
					RowMeters: fRuleMeters,
				},
			},
		};
	}

	// --- Selection + detail data --------------------------------------------------------------------

	onNodeSelect(pSelection)
	{
		this._selected = pSelection;
		// The view repaints the detail and, if the docs side panel is open, refreshes it (fetching this
		// selection's docs as needed). The panel is opened explicitly via the Docs button, not on every select.
		if (this.pict.views['SamplePlan']) { this.pict.views['SamplePlan'].renderDetail(); }
	}

	setDocsMode(pOn)
	{
		this._docsMode = !!pOn;
		if (this.pict.views['SamplePlan']) { this.pict.views['SamplePlan'].renderDetail(); }
	}

	/** Programmatically make a model rule the current selection (used after creating an entry — the tree node may not exist yet). */
	selectRuleRecord(pRule) { this._selected = { Entity: 'Rule', Record: pRule, Key: pRule.IDRule }; }

	/** The layered AcademyContent hashes for a rule: project entry, state spec-year, federal/material. */
	docHashesForRule(pRule)
	{
		const tmpSection = this._sectionCodeForRule(pRule);
		const tmpProcedure = procedureForTest(pRule.Title);
		const tmpHashes = [];
		// The MSM Part II sampling procedure for this test (how to physically sample it) — seeded from the manual.
		if (tmpProcedure) { tmpHashes.push({ Tier: `Sampling procedure · MSM ${tmpProcedure}`, Hash: `SamplePlanProcedure-${tmpProcedure}` }); }
		tmpHashes.push({ Tier: 'Federal', Hash: `SamplePlanFHWA-${pRule.IDMaterial}` });
		// Section sampling schedule from the MSM (seeded per spec Section; manual edition noted in the content).
		tmpHashes.push({ Tier: 'State spec book', Hash: `SamplePlanSpec-${tmpSection}` });
		tmpHashes.push({ Tier: 'This project', Hash: `SamplePlanEntry-${pRule.IDRule}` });
		return tmpHashes;
	}

	_sectionCodeForRule(pRule)
	{
		if (!this._model) { return ''; }
		const tmpLineItem = this._model.lineItems.find((pLI) => pLI.IDLineItem === pRule.IDLineItem);
		return tmpLineItem ? tmpLineItem.IDSection : '';
	}

	/** Doc tiers for ANY selection: a rule's full tiers, or the section schedule for a section / line item / material. */
	docHashesForSelection(pSelection)
	{
		if (!pSelection) { return []; }
		if (pSelection.Entity === 'Rule') { return this.docHashesForRule(pSelection.Record); }
		const tmpSection = this._sectionCodeForSelection(pSelection);
		return tmpSection ? [ { Tier: 'State spec book', Hash: `SamplePlanSpec-${tmpSection}` } ] : [];
	}

	/** Resolve the spec Section code for any selection (sections carry it; line items/materials/rules reach it via their line item). */
	_sectionCodeForSelection(pSelection)
	{
		const tmpRecord = (pSelection && pSelection.Record) || {};
		if (pSelection && (pSelection.Entity === 'Section')) { return tmpRecord.IDSection || tmpRecord.Section || ''; }
		if (tmpRecord.IDSection) { return tmpRecord.IDSection; }
		if (tmpRecord.IDLineItem && this._model)
		{
			const tmpLineItem = this._model.lineItems.find((pLI) => pLI.IDLineItem === tmpRecord.IDLineItem);
			return tmpLineItem ? tmpLineItem.IDSection : '';
		}
		return '';
	}

	/** Resolve a single AcademyContent record by Hash (cached). Returns the record or null. */
	getCachedDoc(pHash) { return this._academyCache[pHash]; }

	/** Fetch + cache AcademyContent rows by hash (idempotent). Resolves true if anything was newly fetched. */
	async _ensureHashes(pHashes)
	{
		let tmpChanged = false;
		await Promise.all((pHashes || []).map(async (pHash) =>
		{
			if (this._academyCache[pHash] !== undefined) { return; }
			try
			{
				const tmpRows = await this._getJSON(`AcademyContents/FilteredTo/FBV~Hash~EQ~${encodeURIComponent(pHash)}`);
				this._academyCache[pHash] = (Array.isArray(tmpRows) && tmpRows.length) ? tmpRows[0] : null;
			}
			catch (pError) { this._academyCache[pHash] = null; }
			tmpChanged = true;
		}));
		return tmpChanged;
	}

	/** Ensure the docs for a selection (a rule's tiers, or a section schedule) are fetched + cached. */
	ensureSelectionDocs(pSelection) { return this._ensureHashes(this.docHashesForSelection(pSelection).map((pEntry) => pEntry.Hash)); }
	/** Ensure a single AcademyContent doc (by hash) is fetched + cached — used by the spec-index navigation. */
	ensureDoc(pHash) { return this._ensureHashes([ pHash ]); }

	/** The index of every seeded sampling-manual doc (Hash + Title), for the spec-navigation root. Cached. */
	async loadSpecIndex()
	{
		if (this._specIndex) { return this._specIndex; }
		try
		{
			const tmpRows = await this._getJSON(`AcademyContents/FilteredTo/FBV~Hash~LK~${encodeURIComponent('SamplePlan%')}/0/500`);
			this._specIndex = Array.isArray(tmpRows) ? tmpRows.map((pRow) => ({ Hash: pRow.Hash, Title: pRow.Title })) : [];
		}
		catch (pError) { this._specIndex = []; }
		return this._specIndex;
	}

	// --- Project picker (front door): a filterable, server-searched list of the caller's projects ------

	/**
	 * Search projects by name for the front-door list. Tenancy is scoped by the server (no client IDCustomer filter —
	 * a Headlight admin configures across customers, so the server owns the scope). Empty term = the first page. The
	 * ≤100 returned rows are name-sorted client-side for a tidy alphabetical list.
	 * @param {String} pTerm
	 * @returns {Promise<Array<{ IDProject: Number, Name: String, Code: String }>>}
	 */
	async searchProjectsAsync(pTerm)
	{
		const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
		let tmpFilter = 'FBV~Deleted~EQ~0';
		if (tmpTerm) { tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`; }
		const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}/0/100`).catch(() => []);
		// The Lite projection carries the display name in `Value` (the search filter still operates on the real Name column)
		// and the FK to the contract in `IDContract` (used to resolve the spec year below).
		const tmpProjects = (Array.isArray(tmpRows) ? tmpRows : [])
			.map((pRow) => (
				{
					IDProject: pRow.IDProject,
					Name: pRow.Value || pRow.Name || pRow.Description || `Project ${pRow.IDProject}`,
					IDContract: pRow.IDContract || 0,
					SpecYear: '',
				}));
		await this._resolveSpecYears(tmpProjects);
		return tmpProjects.sort((pA, pB) => (pA.Name.toLowerCase() < pB.Name.toLowerCase() ? -1 : ((pA.Name.toLowerCase() > pB.Name.toLowerCase()) ? 1 : 0)));
	}

	/**
	 * Fill each project row's SpecYear from its contract — there is no project-level spec year, so the canonical proxy
	 * is Contract.ItemSpecificationYear (the pay-item spec-book year). Batched by distinct contract id, cached across
	 * searches, tolerant of failures, and blank when a project has no contract. Tenancy stays server-managed.
	 */
	async _resolveSpecYears(pProjects)
	{
		const tmpNeeded = {};
		pProjects.forEach((pProject) => { const tmpID = Number(pProject.IDContract); if ((tmpID > 0) && !(tmpID in this._contractSpecYearCache)) { tmpNeeded[tmpID] = true; } });
		const tmpIDs = Object.keys(tmpNeeded);
		const tmpChunkSize = 100;
		for (let i = 0; i < tmpIDs.length; i += tmpChunkSize)
		{
			const tmpChunk = tmpIDs.slice(i, i + tmpChunkSize);
			const tmpContracts = await this._getJSON(`Contracts/FilteredTo/FBL~IDContract~INN~${tmpChunk.join(',')}/0/${tmpChunk.length}`).catch(() => []);
			(Array.isArray(tmpContracts) ? tmpContracts : []).forEach((pContract) =>
			{
				this._contractSpecYearCache[pContract.IDContract] = String(pContract.ItemSpecificationYear || pContract.MaterialsSpecificationYear || '').trim();
			});
			// Any ids that didn't resolve (deleted/inaccessible) → cache blank so we don't refetch them next keystroke.
			tmpChunk.forEach((pID) => { if (!(pID in this._contractSpecYearCache)) { this._contractSpecYearCache[pID] = ''; } });
		}
		pProjects.forEach((pProject) => { pProject.SpecYear = (Number(pProject.IDContract) > 0) ? (this._contractSpecYearCache[pProject.IDContract] || '') : ''; });
	}

	// --- Edit mode: full rule record, dropdown vocabularies, live counts, save --------------------------

	get features() { return this._features || DEFAULT_FEATURES; }
	get unitsOptions() { return this._unitsOptions || []; }
	get intentOptions() { return INTENT_OPTIONS.slice(); }

	/** Distinct, non-empty, case-insensitively sorted UnitsImperial across the loaded materials (the Units vocabulary). */
	_distinctUnits(pMaterials)
	{
		const tmpSeen = {};
		(pMaterials || []).forEach((pMaterial) =>
		{
			const tmpUnit = (pMaterial && (pMaterial.UnitsImperial != null)) ? String(pMaterial.UnitsImperial).trim() : '';
			if (tmpUnit !== '') { tmpSeen[tmpUnit] = true; }
		});
		return Object.keys(tmpSeen).sort((pA, pB) => (pA.toLowerCase() < pB.toLowerCase() ? -1 : 1));
	}

	/** Best-effort load of the SamplePlan config (same source the Vue app reads); merged over the shared defaults. */
	async _loadFeaturesAsync()
	{
		try
		{
			const tmpConfig = await this._getJSON('Configuration/For/SamplePlan/HeadLight');
			return Object.assign({}, DEFAULT_FEATURES, (tmpConfig && tmpConfig.Features) || {});
		}
		catch (pError) { return Object.assign({}, DEFAULT_FEATURES); }
	}

	/**
	 * Whether the current user may edit sample-plan rules (SamplePlanEntry/Update). Server-authoritative and
	 * fail-open before the capability map loads — matching the app's other capability gates; the server enforces
	 * the write (tenancy included), and a denied save surfaces as an inline error.
	 */
	canEditRule()
	{
		const tmpApp = this.pict.PictApplication;
		const tmpService = (tmpApp && tmpApp.EffectivePermissionsService) || (this.pict.fable && this.pict.fable.EffectivePermissionsService);
		if (tmpService && (typeof tmpService.can === 'function')) { return !!tmpService.can('SamplePlanEntry', 'Update'); }
		return true;
	}

	/** The raw PLIMT row for a rule, if already fetched/cached (the full editable field set the lossy model omits). */
	getRawRule(pIDRule) { return this._rawRuleCache[pIDRule]; }

	/**
	 * Fetch the full editable PLIMT row for a Rule selection — the in-memory model is a lossy projection, so VIEW
	 * (full field set) and EDIT both read the raw record. Cached by IDRule; tenancy is enforced server-side.
	 */
	async fetchRuleRecordAsync(pSelection)
	{
		if (!pSelection || (pSelection.Entity !== 'Rule')) { return null; }
		const tmpIDRule = (pSelection.Record || {}).IDRule;
		if (tmpIDRule == null) { return null; }
		if (this._rawRuleCache[tmpIDRule]) { return this._rawRuleCache[tmpIDRule]; }
		const tmpRecord = await this._getJSON(`ProjectLineItemMaterialTest/${tmpIDRule}`);
		if (tmpRecord && (tmpRecord.IDProjectLineItemMaterialTest != null)) { this._rawRuleCache[tmpIDRule] = tmpRecord; return tmpRecord; }
		return null;
	}

	/** Lazily load the Test catalog for the Test dropdown (server scopes it to what the user may see). Cached. */
	async ensureTestOptionsAsync()
	{
		if (this._testOptions) { return this._testOptions; }
		try
		{
			const tmpRows = await this._getJSON('Tests/0/2000');
			this._testOptions = Array.isArray(tmpRows) ? tmpRows : [];
		}
		catch (pError)
		{
			this.pict.log.warn('SamplePlan: could not load the Test catalog for the Test dropdown', pError);
			this._testOptions = [];
		}
		return this._testOptions;
	}

	/** Test dropdown options: a "(no test selected)" sentinel + the catalog, always keeping the current pick visible. */
	testOptionsFor(pCurrentIDTest)
	{
		const tmpOptions = [ { IDTest: 0, Name: '(no test selected)' } ];
		const tmpSeen = { 0: true };
		(this._testOptions || []).forEach((pTest) =>
		{
			tmpSeen[pTest.IDTest] = true;
			tmpOptions.push({ IDTest: pTest.IDTest, Name: pTest.Name || pTest.Hash || `Test ${pTest.IDTest}` });
		});
		if (pCurrentIDTest && !tmpSeen[pCurrentIDTest])
		{
			const tmpModelRule = this._model && this._model.rules.find((pRule) => Number(pRule.IDTest) === Number(pCurrentIDTest));
			tmpOptions.push({ IDTest: pCurrentIDTest, Name: (tmpModelRule && tmpModelRule.TestHash) ? tmpModelRule.TestHash : `Test ${pCurrentIDTest}` });
		}
		return tmpOptions;
	}

	/**
	 * Refresh every rule's required/planned/created counts from the SERVER after an edit or create. A saved PLIMT's
	 * ComputedRequiredSampleCount is stale until the endpoint recomputes it, and the count is NEVER derived client-side
	 * (the server's PLIMTCalculator uses placed quantities + temporal/plant data we don't have here). So we re-fetch
	 * SamplePlan/Lite — which recomputes the whole plan server-side — and copy the fresh counts onto the model rules,
	 * then re-roll the aggregates + satisfaction. Best-effort: a failed refresh leaves the last-known counts.
	 * @returns {Promise<void>}
	 */
	async _refreshPlanCountsAsync()
	{
		if (!this._model || !this._idProject) { return; }
		let tmpLite;
		try { tmpLite = await this._getJSON(`SamplePlan/Lite/By/IDProject/${this._idProject}`); }
		catch (pError) { this.pict.log.warn(`SamplePlan: could not refresh sample counts from the server: ${pError && pError.message ? pError.message : pError}`); return; }
		const tmpByID = {};
		((tmpLite && tmpLite.ProjectLineItemMaterialTests) || []).forEach((pPLIMT) => { tmpByID[Number(pPLIMT.IDProjectLineItemMaterialTest)] = pPLIMT; });
		const fNum = (pValue) => { const tmp = Number(pValue); return Number.isFinite(tmp) ? tmp : 0; };
		(this._model.rules || []).forEach((pRule) =>
		{
			const tmpPLIMT = tmpByID[Number(pRule.IDRule)];
			if (!tmpPLIMT) { return; }
			const tmpCounts = resolvePlimtCounts(tmpPLIMT);
			pRule.Required = tmpCounts.Required;
			pRule.Planned = tmpCounts.Planned;
			pRule.Created = fNum(tmpPLIMT.CreatedSampleCount);
			pRule.Passing = fNum(tmpPLIMT.PassingSampleCount);
		});
		restampRollups(this._model);
		if (this._model.plan) { this.workspaceState.PlanText = this._model.plan.SatisfactionText; }
	}

	/**
	 * Refresh ONE rule's counts from the server after an edit/create, cheaply: POST the entry to the single-entry
	 * SamplePlan/CalculateEntry endpoint (same authoritative PLIMTCalculator, no whole-plan re-pricing) and copy the
	 * fresh Computed* onto the rule + its raw-cache row, then re-roll. If that endpoint isn't available (older API),
	 * fall back to the whole-plan _refreshPlanCountsAsync. Counts are NEVER computed client-side either way.
	 * @param {Object} pRule the in-memory model rule @param {Object} pRawPLIMT the saved raw PLIMT (edited fields)
	 * @returns {Promise<void>}
	 */
	async _refreshRuleCountsAsync(pRule, pRawPLIMT)
	{
		if (!this._model || !this._idProject || !pRule || !pRawPLIMT) { return this._refreshPlanCountsAsync(); }
		let tmpResult;
		try { tmpResult = await this._postJSON(`SamplePlan/CalculateEntry/By/IDProject/${this._idProject}`, pRawPLIMT); }
		catch (pError) { return this._refreshPlanCountsAsync(); }
		if (!tmpResult || tmpResult.Error || (tmpResult.ComputedRequiredSampleCount == null)) { return this._refreshPlanCountsAsync(); }
		// Fold the fresh server Computed* into the raw-cache row, then resolve counts the same way the load path does
		// (so a manual override is honored identically). Created/Passing come from actual samples — an edit doesn't move them.
		const tmpRaw = (this._rawRuleCache[pRule.IDRule] = this._rawRuleCache[pRule.IDRule] || pRawPLIMT);
		tmpRaw.ComputedRequiredSampleCount = tmpResult.ComputedRequiredSampleCount;
		tmpRaw.ComputedPlannedSampleCount = tmpResult.ComputedPlannedSampleCount;
		const tmpCounts = resolvePlimtCounts(tmpRaw);
		pRule.Required = tmpCounts.Required;
		pRule.Planned = tmpCounts.Planned;
		restampRollups(this._model);
		if (this._model.plan) { this.workspaceState.PlanText = this._model.plan.SatisfactionText; }
	}

	/**
	 * The SERVER's computed required/planned counts for a rule (the raw PLIMT's Computed*SampleCount, cached fresh from
	 * SamplePlan/Lite). Used by the editor's readouts — NOT a client-side recompute. While a rule's Rate/Frequency is
	 * being edited these show the last server value; the real counts are refreshed from the endpoint on save.
	 * @returns {{ ComputedRequiredSampleCount: Number, ComputedPlannedSampleCount: Number }}
	 */
	serverCountsFor(pRecord)
	{
		const fNum = (pValue) => { const tmp = Number(pValue); return Number.isFinite(tmp) ? tmp : 0; };
		const tmpID = pRecord && pRecord.IDProjectLineItemMaterialTest;
		const tmpRaw = ((tmpID != null) && this._rawRuleCache[tmpID]) || pRecord || {};
		return { ComputedRequiredSampleCount: fNum(tmpRaw.ComputedRequiredSampleCount), ComputedPlannedSampleCount: fNum(tmpRaw.ComputedPlannedSampleCount) };
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/**
	 * Persist an edited rule. Mirrors the legacy save: strip transient/_-prefixed keys + Material/LineItem/
	 * IDMaterialLineItemJoin, PUT the array, partition the response by .Error. On success, patch the in-memory
	 * model rule (== the live selection record) so the detail reflects the saved values immediately.
	 * @returns {Promise<{ ok: Boolean, error: any, record: any }>}
	 */
	async saveRuleAsync(pRecord)
	{
		const tmpClean = JSON.parse(JSON.stringify(pRecord));
		Object.keys(tmpClean).forEach((pKey) =>
		{
			if ((pKey.charAt(0) === '_') || (pKey === 'Material') || (pKey === 'LineItem') || (pKey === 'IDMaterialLineItemJoin')) { delete tmpClean[pKey]; }
		});
		let tmpResult;
		try { tmpResult = await this._putJSON('ProjectLineItemMaterialTests', [ tmpClean ]); }
		catch (pError) { return { ok: false, error: (pError && pError.message) ? pError.message : String(pError), record: null }; }
		const tmpRows = Array.isArray(tmpResult) ? tmpResult : (tmpResult ? [ tmpResult ] : []);
		const tmpErrant = tmpRows.filter((pRow) => pRow && pRow.Error);
		if (tmpErrant.length) { return { ok: false, error: tmpErrant[0].Error || 'The server rejected the save.', record: null }; }
		const tmpSaved = tmpRows.find((pRow) => pRow && !pRow.Error) || tmpClean;
		this._rawRuleCache[tmpSaved.IDProjectLineItemMaterialTest || tmpClean.IDProjectLineItemMaterialTest] = tmpSaved;
		this._patchModelRule(tmpSaved);
		// The edit changed Rate/Frequency/etc., so the required/planned counts changed — pull the fresh, server-computed
		// counts from the endpoint (never recomputed here) and re-roll the ancestor aggregates + plan satisfaction.
		const tmpEditedRule = this._model.rules.find((pRule) => Number(pRule.IDRule) === Number(tmpSaved.IDProjectLineItemMaterialTest));
		await this._refreshRuleCountsAsync(tmpEditedRule, tmpSaved);
		return { ok: true, error: null, record: tmpSaved };
	}

	/** Update the in-memory model rule (the same object the explorer + detail read) from a saved raw PLIMT. */
	_patchModelRule(pSaved)
	{
		if (!this._model) { return; }
		const tmpRule = this._model.rules.find((pRule) => Number(pRule.IDRule) === Number(pSaved.IDProjectLineItemMaterialTest));
		if (!tmpRule) { return; }
		const fNum = (pValue) => { const tmp = Number(pValue); return Number.isFinite(tmp) ? tmp : 0; };
		const fStr = (pValue) => ((pValue == null) ? '' : String(pValue));
		tmpRule.Frequency = fNum(pSaved.Frequency);
		tmpRule.Rate = fNum(pSaved.Rate) || 1;
		tmpRule.ConversionFactor = fNum(pSaved.ConversionFactor) || 1;
		tmpRule.Units = fStr(pSaved.Units);
		tmpRule.Intent = fStr(pSaved.Intent);
		tmpRule.SamplingInstructions = fStr(pSaved.SamplingInstructions);
		tmpRule.Remarks = fStr(pSaved.Remarks);
		tmpRule.SampleType = fStr(pSaved.SampleType) || tmpRule.SampleType;
		tmpRule.IDTest = pSaved.IDTest;
		tmpRule.Title = fStr(pSaved.TestDescription || pSaved.SampleDescription) || tmpRule.Title;
		// Refresh the test code/label to match the saved IDTest. Clearing the test (IDTest 0) blanks the hash (mirroring
		// the model builder); a valid id that simply isn't in the loaded catalog keeps the existing hash as best-available.
		const tmpTest = (this._testOptions || []).find((pTest) => Number(pTest.IDTest) === Number(pSaved.IDTest));
		if (Number(pSaved.IDTest) === 0) { tmpRule.TestHash = ''; tmpRule.TestLabel = tmpRule.SampleType; }
		else if (tmpTest) { tmpRule.TestHash = fStr(tmpTest.Hash); tmpRule.TestLabel = tmpRule.TestHash ? `${tmpRule.TestHash}  ·  ${tmpRule.SampleType}` : tmpRule.SampleType; }
		// Kind reflects the saved rule shape; the required/planned counts + satisfaction text are refreshed from the
		// server by _refreshPlanCountsAsync (called right after this) — never computed client-side.
		const tmpIsCert = (fNum(pSaved.CertificateRequired) === 1);
		const tmpIsPerProject = !tmpIsCert && (fNum(pSaved.Frequency) === 999999);
		tmpRule.Kind = tmpIsCert ? 'cert' : (tmpIsPerProject ? 'perproject' : 'count');
	}

	// --- Add: new entries (PLIMT) and new materials (MLIJ) -----------------------------------------------

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/** Lazily load the customer's Sample Type vocabulary (server-scoped). Cached. */
	async ensureSampleTypesAsync()
	{
		if (this._sampleTypes) { return this._sampleTypes; }
		try
		{
			const tmpRows = await this._getJSON('SampleTypes/0/200');
			this._sampleTypes = (Array.isArray(tmpRows) ? tmpRows : []).filter((pRow) => (Number(pRow.Deleted) !== 1));
		}
		catch (pError) { this._sampleTypes = []; }
		return this._sampleTypes;
	}

	/** Sample Type dropdown options — value is the stored code (Hash), label is "Name (CODE)". */
	sampleTypeOptions()
	{
		return (this._sampleTypes || []).map((pType) => ({ value: pType.Hash, label: pType.Name ? `${pType.Name} (${pType.Hash})` : pType.Hash }));
	}

	/** A sensible default unit for a new entry: the material's unit, else the line item's, else 'VARI' (mirrors the legacy
	 *  add). An empty unit is NOT neutral — the calculator treats '' as a "1 per project" cadence, so never default to ''. */
	defaultUnitsForMaterial(pIDLineItem, pIDMaterial)
	{
		const fStr = (pValue) => ((pValue == null) ? '' : String(pValue).trim());
		const tmpMaterial = this._rawMaterialByID[pIDMaterial] || {};
		const tmpLineItem = this._rawLineItemByID[pIDLineItem] || {};
		return fStr(tmpMaterial.UnitsImperial) || fStr(tmpLineItem.Units || tmpLineItem.UnitsImperial) || 'VARI';
	}

	/** Build an in-memory model rule from a raw PLIMT (mirrors buildSamplePlanModel's rule shape), for post-create splicing. */
	_buildModelRuleFromRaw(pRaw)
	{
		const fNum = (pValue) => { const tmp = Number(pValue); return Number.isFinite(tmp) ? tmp : 0; };
		const fStr = (pValue) => ((pValue == null) ? '' : String(pValue));
		const tmpTest = (this._testOptions || []).find((pTest) => Number(pTest.IDTest) === Number(pRaw.IDTest));
		const tmpTestHash = tmpTest ? fStr(tmpTest.Hash) : '';
		const tmpSampleType = fStr(pRaw.SampleType) || 'ACPT';
		// Counts are seeded from the saved row's server values (0 if not yet computed) and then replaced with the
		// freshly server-computed counts by _refreshPlanCountsAsync — never derived client-side.
		const tmpCounts = resolvePlimtCounts(pRaw);
		const tmpCreated = fNum(pRaw.CreatedSampleCount);
		const tmpIsCert = (fNum(pRaw.CertificateRequired) === 1);
		const tmpIsPerProject = !tmpIsCert && (fNum(pRaw.Frequency) === 999999);
		const tmpRule =
		{
			IDRule: pRaw.IDProjectLineItemMaterialTest,
			IDLineItem: pRaw.IDLineItem,
			IDMaterial: pRaw.IDMaterial,
			MaterialKey: `${pRaw.IDLineItem}:${pRaw.IDMaterial}`,
			IDTest: pRaw.IDTest,
			TestHash: tmpTestHash,
			Title: fStr(pRaw.TestDescription || pRaw.SampleDescription) || 'Material Acceptance',
			SampleType: tmpSampleType,
			TestLabel: tmpTestHash ? `${tmpTestHash}  ·  ${tmpSampleType}` : tmpSampleType,
			Frequency: fNum(pRaw.Frequency),
			Rate: fNum(pRaw.Rate) || 1,
			ConversionFactor: fNum(pRaw.ConversionFactor) || 1,
			Units: fStr(pRaw.Units),
			Intent: fStr(pRaw.Intent),
			SamplingInstructions: fStr(pRaw.SamplingInstructions),
			Remarks: fStr(pRaw.Remarks),
			Kind: tmpIsCert ? 'cert' : (tmpIsPerProject ? 'perproject' : 'count'),
			Required: tmpCounts.Required,
			Planned: tmpCounts.Planned,
			Created: tmpCreated,
			Passing: fNum(pRaw.PassingSampleCount),
			CertOnFile: (tmpCreated > 0),
		};
		tmpRule.SatisfactionText = tmpIsCert ? 'By certificate' : (tmpIsPerProject ? '1 / project' : `${tmpRule.Created}/${tmpRule.Required} samples`);
		return tmpRule;
	}

	/**
	 * Create a new sample-plan entry (PLIMT) under an existing material. POSTs the stub, then splices the returned row
	 * into the model + the mutable Tests index + rollups so the tree and detail reflect it without a reload.
	 * @returns {Promise<{ ok: Boolean, error: any, rule: any }>}
	 */
	async createEntryAsync(pStub)
	{
		const tmpClean = JSON.parse(JSON.stringify(pStub));
		Object.keys(tmpClean).forEach((pKey) => { if ((pKey.charAt(0) === '_') || (pKey === 'Material') || (pKey === 'LineItem') || (pKey === 'IDMaterialLineItemJoin')) { delete tmpClean[pKey]; } });
		// PLIMT.IDProject is a real column the Lite reload filters on (SamplePlan/Lite …where('IDProject')). The server
		// does NOT backfill it, so a created entry without it (IDProject 0) would vanish on the next reload — set it.
		tmpClean.IDProject = Number(this._idProject);
		let tmpResult;
		try { tmpResult = await this._postJSON('ProjectLineItemMaterialTests', [ tmpClean ]); }
		catch (pError) { return { ok: false, error: (pError && pError.message) ? pError.message : String(pError), rule: null }; }
		const tmpRows = Array.isArray(tmpResult) ? tmpResult : (tmpResult ? [ tmpResult ] : []);
		const tmpErrant = tmpRows.filter((pRow) => pRow && pRow.Error);
		if (tmpErrant.length) { return { ok: false, error: tmpErrant[0].Error || 'The server rejected the new entry.', rule: null }; }
		const tmpSaved = tmpRows.find((pRow) => pRow && !pRow.Error && (pRow.IDProjectLineItemMaterialTest != null));
		if (!tmpSaved) { return { ok: false, error: 'The server did not return the created entry.', rule: null }; }
		this._rawRuleCache[tmpSaved.IDProjectLineItemMaterialTest] = tmpSaved;
		const tmpRule = this._buildModelRuleFromRaw(tmpSaved);
		this._model.rules.push(tmpRule);
		(this._rulesByMaterialKey[tmpRule.MaterialKey] = this._rulesByMaterialKey[tmpRule.MaterialKey] || []).push(tmpRule);
		// Pull the new entry's server-computed counts (and re-roll) from the endpoint — never computed client-side.
		await this._refreshRuleCountsAsync(tmpRule, tmpSaved);
		this.workspaceState.RuleCount = this._model.rules.length;   // keep the header's "N sample plan entries" current
		return { ok: true, error: null, rule: tmpRule };
	}

	/** Search the active materials catalog by name/code, excluding materials already on the given line item. Server-scoped. */
	async searchMaterialsAsync(pTerm, pIDLineItem)
	{
		// Strip the structural tilde (FoxHound stanza delimiter) so a typed "~" can't corrupt the filter.
		const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
		let tmpFilter = 'FBV~Active~EQ~1';
		if (tmpTerm)
		{
			const tmpLike = encodeURIComponent(`%${tmpTerm.replace(/\*/g, '%')}%`);
			tmpFilter += `~FOP~0~(~0~FBVOR~Name~LK~${tmpLike}~FBVOR~MaterialCode~LK~${tmpLike}~FCP~0~)~0`;
		}
		tmpFilter += '~FSF~Name~ASC~0';
		const tmpRows = await this._getJSON(`Materials/FilteredTo/${tmpFilter}/0/100`).catch(() => []);
		const tmpExisting = {};
		(this._materialsByLineItem[pIDLineItem] || []).forEach((pMaterial) => { tmpExisting[pMaterial.IDMaterial] = true; });
		return (Array.isArray(tmpRows) ? tmpRows : [])
			.filter((pRow) => !tmpExisting[pRow.IDMaterial])
			.map((pRow) => ({ IDMaterial: pRow.IDMaterial, Name: pRow.Name || `Material ${pRow.IDMaterial}`, MaterialCode: pRow.MaterialCode || pRow.CatalogCode || '', UnitsImperial: pRow.UnitsImperial || '' }));
	}

	/**
	 * Attach a material to a line item (create its MaterialLineItemJoin). Resolves Units (material → line item → 'VARI')
	 * and best-effort special instructions, then splices a new material node into the model + tree indexes.
	 * @returns {Promise<{ ok: Boolean, error: any, material: any }>}
	 */
	async addMaterialAsync(pLineItem, pMaterial)
	{
		const fStr = (pValue) => ((pValue == null) ? '' : String(pValue));
		const tmpRawLineItem = this._rawLineItemByID[pLineItem.IDLineItem] || {};
		const tmpUnits = fStr(pMaterial.UnitsImperial) || fStr(tmpRawLineItem.Units || tmpRawLineItem.UnitsImperial) || 'VARI';
		let tmpSpecial = '';
		try
		{
			const tmpTSMJ = await this._getJSON(`TestSpecificationMaterialJoins/FilteredTo/FBV~IDMaterial~EQ~${pMaterial.IDMaterial}~FSF~Year~DESC~0/0/1`);
			if (Array.isArray(tmpTSMJ) && tmpTSMJ.length) { tmpSpecial = fStr(tmpTSMJ[0].SpecialInstructions); }
		}
		catch (pError) { /* special instructions are best-effort */ }
		let tmpResult;
		try { tmpResult = await this._postJSON('MaterialLineItemJoins', [ { IDLineItem: pLineItem.IDLineItem, IDMaterial: pMaterial.IDMaterial, Units: tmpUnits, SpecialInstructions: tmpSpecial } ]); }
		catch (pError) { return { ok: false, error: (pError && pError.message) ? pError.message : String(pError), material: null }; }
		const tmpRows = Array.isArray(tmpResult) ? tmpResult : (tmpResult ? [ tmpResult ] : []);
		const tmpErrant = tmpRows.filter((pRow) => pRow && pRow.Error);
		if (tmpErrant.length) { return { ok: false, error: tmpErrant[0].Error || 'The server rejected the new material.', material: null }; }
		// Retain the raw material (units vocab + calculator parent lookup) and splice a model material node into the tree.
		this._rawMaterialByID[pMaterial.IDMaterial] = this._rawMaterialByID[pMaterial.IDMaterial] || { IDMaterial: pMaterial.IDMaterial, Name: pMaterial.Name, MaterialCode: pMaterial.MaterialCode, UnitsImperial: pMaterial.UnitsImperial };
		const tmpKey = `${pLineItem.IDLineItem}:${pMaterial.IDMaterial}`;
		const tmpMaterialNode =
		{
			Key: tmpKey, IDLineItem: pLineItem.IDLineItem, IDMaterial: pMaterial.IDMaterial,
			Name: fStr(pMaterial.Name) || `Material ${pMaterial.IDMaterial}`, MaterialCode: fStr(pMaterial.MaterialCode),
			SamplesRequired: 0, SamplesCreated: 0, CertCount: 0, CertPending: 0, EntryCount: 0, SatisfactionText: '',
		};
		this._model.materials.push(tmpMaterialNode);
		(this._materialsByLineItem[pLineItem.IDLineItem] = this._materialsByLineItem[pLineItem.IDLineItem] || []).push(tmpMaterialNode);
		this._rulesByMaterialKey[tmpKey] = this._rulesByMaterialKey[tmpKey] || [];
		return { ok: true, error: null, material: tmpMaterialNode };
	}
}

SamplePlanProvider.default_configuration = { ProviderIdentifier: 'SamplePlan' };

module.exports = SamplePlanProvider;
module.exports.default_configuration = SamplePlanProvider.default_configuration;
